import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { prysmDB } from '../utils/database';

function Welcome({ user, error, isLoading }) {
  const navigate = useNavigate();
  const [isNoticeOpen, setIsNoticeOpen] = useState(false);

  // Log session data when component mounts (read-only)
  useEffect(() => {
    const loadSessionData = async () => {
      try {
        const registrationData = await prysmDB.get('registration_data', 'current_registration');
        if (registrationData) {
          console.log('[Welcome] Loaded session data from storage:', registrationData);
        }
      } catch (error) {
        console.error('[Welcome] Error reading session data from storage:', error);
      }
    };

    loadSessionData();
  }, []);

  const getErrorAction = () => {
    if (error?.type === 'connection') {
      return (
        <div className="mt-4">
          <div className="space-y-2">
            <p className="text-sm text-gray-600">To fix this:</p>
            <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
              <li>Try Refreshing the page</li>
              <li>Check if the URL is correct</li>
              <li>Verify your network connection</li>
            </ol>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 w-full shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Retry Connection
          </button>
        </div>
      );
    }

    return (
      <button
        onClick={() => window.location.reload()}
        className="mt-3 px-4 py-2 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-xl hover:from-red-700 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
      >
        Try Again
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-gray-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-2xl p-8 transform transition-all duration-500 hover:scale-[1.02] border border-gray-700">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-500 mb-4 animate-fade-in">
              OurPrysm Registration
            </h1>
            <div className="h-1 w-20 bg-gradient-to-r from-pink-500 to-purple-600 mx-auto rounded-full animate-glow"></div>
          </div>

          <div className="flex items-center justify-center gap-2 mb-6">
            <span className="text-sm text-gray-300">Married Couples Register Individually</span>
            <button
              type="button"
              aria-label="Learn more"
              onClick={() => setIsNoticeOpen(true)}
              className="w-5 h-5 flex items-center justify-center rounded-full border border-gray-500 text-gray-300 hover:text-white hover:border-gray-400 transition-colors"
            >
              ?
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center mb-8">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-pink-500 border-t-transparent"></div>
              <span className="ml-3 text-gray-300 font-medium">Loading...</span>
            </div>
          ) : error ? (
            <div className="mb-8 p-6 bg-gray-700/50 backdrop-blur-sm rounded-xl border border-gray-600">
              <p className="text-gray-200 font-medium">
                {error.type === 'connection' ? 'Connection Error' :
                  error.type === 'token' ? 'Session Expired' :
                    error.type === 'creation' ? 'Session Creation Failed' :
                      'Error Loading User Data'}
              </p>
              <p className="text-gray-400 text-sm mt-2">{error.message}</p>
              {getErrorAction()}
            </div>
          ) : null}

          <div className="space-y-6">
            <div className="group">
              <button
                onClick={() => navigate('/register')}
                className="w-full flex items-center space-x-4 p-4 bg-gray-700/50 hover:bg-gray-700/70 rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg border border-gray-600 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 transition-transform duration-300 relative z-10">
                  1
                </div>
                <span className="text-gray-200 font-medium text-lg relative z-10">Fill out the registration</span>
              </button>
            </div>

            <div className="group">
              <button
                onClick={() => navigate('/square-payment')}
                className="w-full flex items-center space-x-4 p-4 bg-gray-700/50 hover:bg-gray-700/70 rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg border border-gray-600 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 transition-transform duration-300 relative z-10">
                  2
                </div>
                <span className="text-gray-200 font-medium text-lg relative z-10">Pick your plan</span>
              </button>
            </div>

            <div className="group">
              <button
                onClick={() => navigate('/upload-selfie')}
                className="w-full flex items-center space-x-4 p-4 bg-gray-700/50 hover:bg-gray-700/70 rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg border border-gray-600 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 transition-transform duration-300 relative z-10">
                  3
                </div>
                <span className="text-gray-200 font-medium text-lg relative z-10">Upload a selfie</span>
              </button>
            </div>

            <div className="group">
              <button
                onClick={() => navigate('/upload-license')}
                className="w-full flex items-center space-x-4 p-4 bg-gray-700/50 hover:bg-gray-700/70 rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg border border-gray-600 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 transition-transform duration-300 relative z-10">
                  4
                </div>
                <span className="text-gray-200 font-medium text-lg relative z-10">Upload Driver's License</span>
              </button>
            </div>

            <div className="group">
              <button
                onClick={() => navigate('/sms-verification')}
                className="w-full flex items-center space-x-4 p-4 bg-gray-700/50 hover:bg-gray-700/70 rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg border border-gray-600 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 transition-transform duration-300 relative z-10">
                  5
                </div>
                <span className="text-gray-200 font-medium text-lg relative z-10">Verify Mobile</span>
              </button>
            </div>

            <div className="group">
              <button
                onClick={() => navigate('/checkout')}
                className="w-full flex items-center space-x-4 p-4 bg-gray-700/50 hover:bg-gray-700/70 rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg border border-gray-600 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 transition-transform duration-300 relative z-10">
                  6
                </div>
                <span className="text-gray-200 font-medium text-lg relative z-10">Submit Information</span>
              </button>
            </div>
          </div>

          <p className="text-gray-400 text-center text-sm animate-pulse">
            Complete all steps to get started
          </p>

          {user && (
            <div className="pt-4 border-t border-gray-700">
              <p className="text-gray-500 text-center text-sm">
                User ID: {user.uuid}
              </p>
            </div>
          )}

          {isNoticeOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/60" onClick={() => setIsNoticeOpen(false)}></div>
              <div className="relative z-10 w-full max-w-md mx-4 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-100">Registration Notice</h2>
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={() => setIsNoticeOpen(false)}
                    className="text-gray-400 hover:text-gray-200"
                  >
                    ×
                  </button>
                </div>
                <div className="text-sm text-gray-300 space-y-3">
                  <p>
                    Even as a couple, you each need your own paid account. Prysm protects and supports you as individuals, because experience shows partners don’t always answer the same way. Our profilers and matchmakers need that clarity to give you, your partner, and your potential partner the best possible match.
                  </p>
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsNoticeOpen(false)}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx="true">{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        @keyframes glow {
          0% {
            box-shadow: 0 0 5px rgba(147, 51, 234, 0.2), 0 0 10px rgba(147, 51, 234, 0.2);
          }
          50% {
            box-shadow: 0 0 20px rgba(147, 51, 234, 0.4), 0 0 30px rgba(147, 51, 234, 0.4);
          }
          100% {
            box-shadow: 0 0 5px rgba(147, 51, 234, 0.2), 0 0 10px rgba(147, 51, 234, 0.2);
          }
        }
        .animate-glow {
          animation: glow 2s infinite;
        }
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
      `}</style>
    </div>
  );
}

export default Welcome; 